import React, { useState, useEffect, useCallback, useMemo } from 'react';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';

const toFa = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s).replace(/[۰-۹]/g,d=>'۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const numFmt = n => n ? Number(n).toLocaleString('fa-IR') : '•';
const si = v => { try{return parseInt(toEng(String(v||0)),10)||0;}catch{return 0;} };

/* فراخوانی API + رفرش توکن */
async function API(path, opts={}){
  const doFetch=()=>fetch(`/api/accounting${path}`,{
    headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts});
  let res=await doFetch();
  if(res.status===401){
    const rr=await fetch('/api/auth/token/refresh/',{method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({refresh:localStorage.getItem('refresh')})});
    if(rr.ok){const d=await rr.json();localStorage.setItem('access',d.access);res=await doFetch();}
  }
  return res.json();
}

const MODES = [
  { key:'zemanat',  label:'وصول ضمانت‌نامه', icon:'📥', color:'#1b5e20' },
  { key:'decrease', label:'تقلیل/افزایش',    icon:'📉', color:'#e65100' },
  { key:'cancel',   label:'ابطال',            icon:'🚫', color:'#b71c1c' },
  { key:'give',     label:'ضبط',              icon:'🔒', color:'#4a148c' },
];

export default function ZemanatReadyModal({ year, onClose, onAdded }) {
  const [mode,       setMode]       = useState('zemanat');
  const [rawRows,    setRawRows]    = useState([]);
  const [checked,    setChecked]    = useState({});
  const [readyNo,    setReadyNo]    = useState('');
  const [docDate,    setDocDate]    = useState('');
  const [fromDate,   setFromDate]   = useState('1394/07/01');
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [toast,      setToast]      = useState('');

  /* عرض ستون‌ها */
  const { widths, TH } = useColumnResize(
    mode === 'decrease'
      ? [40,60,60,110,100,100,80,90,100,100,100,140,100]
      : [40,60,60,110,80,80,100,80,80,100,140,100]
  );

  const {
    visibleRows, search, setSearch, hasMore,
    filteredCount, shownCount, LoadMoreButton, resetPagination,
  } = useTablePagination({
    allRows: rawRows,
    pageSize: 200,
    searchFields: ['contractor_name', 'contract_display', 'zemanat_no', 'sepam', 'serial_no_str'],
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),4000); };

  useEffect(() => {
    API(`/documents/next-no/?year=${year}`).then(j => {
      if (j.ok) setDocDate(j.last_date || '');
    });
  }, [year]);

  /* خواندن فهرست از سرور */
  const load = useCallback(async () => {
    setLoading(true); setError(''); setRawRows([]); setChecked({}); resetPagination();
    const j = await API(`/auto-document/zemanat/ready/?mode=${mode}&from_date=${fromDate}`);
    setLoading(false);
    if (j.ok) {
      setRawRows(j.data.map(r => ({ ...r, serial_no_str: String(r.serial_no || '') })));
    } else setError(j.msg);
  }, [mode, fromDate, resetPagination]);

  useEffect(() => { load(); }, [load]);

  // ── row key بسته به mode ──
  const rowKey = r => mode === 'decrease' ? r.zemanat_transaction_id : r.id;

  const toggleAll = val => {
    const next = { ...checked };
    visibleRows.forEach(r => { const k = rowKey(r); if (val) next[k] = true; else delete next[k]; });
    setChecked(val ? { ...checked, ...next } : (() => { visibleRows.forEach(r => delete next[rowKey(r)]); return next; })());
  };

  const toggleOne = (k, val) => setChecked(c => {
    const n = { ...c }; if (val) n[k] = true; else delete n[k]; return n;
  });

  const selectedRows = rawRows.filter(r => checked[rowKey(r)]);
  const totalAmount = selectedRows.reduce((s,r) => {
    if (mode === 'decrease') return s + Math.abs(si(r.change_amount));
    return s + si(r.amount);
  }, 0);
  const isNewDoc = si(readyNo) === 0;
  const allVisibleChecked = visibleRows.length > 0 && visibleRows.every(r => checked[rowKey(r)]);
  const modeInfo = MODES.find(m => m.key === mode);

  /* افزودن به سند */
  const addToDoc = async () => {
    if (!selectedRows.length) { setError('هیچ ردیفی انتخاب نشده'); return; }
    if (isNewDoc && !docDate) { setError('تاریخ سند جدید الزامیست'); return; }
    setSaving(true); setError('');
    const j = await API('/auto-document/zemanat/add/', {
      method: 'POST',
      body: JSON.stringify({
        financial_year: year, ready_document_no: si(readyNo),
        doc_date: docDate, mode,
        rows: rawRows.map(r => ({ ...r, selected: !!checked[rowKey(r)] })),
      }),
    });
    setSaving(false);
    if (j.ok) { showToast(j.msg); onAdded && onAdded(); load(); }
    else setError(j.msg);
  };

  // ── ستون‌ها بسته به mode ──
  const getAmountLabel = () => {
    if (mode === 'decrease') return null; // ستون‌های خاص
    return 'مبلغ';
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e=>e.stopPropagation()}>
        <div style={{...s.hdr, background:`linear-gradient(135deg,#0a1628,${modeInfo.color}88)`}}>
          <div>
            <div style={{fontSize:'15px',fontWeight:'700',color:'#fff'}}>
              {modeInfo.icon} ضمانت‌نامه — {modeInfo.label}
            </div>
            <div style={{fontSize:'11px',color:'rgba(255,255,255,0.6)',marginTop:'2px'}}>
              حساب انتظامی 810/820 — سال مالی {toFa(year)}
            </div>
          </div>
          <button onClick={onClose} style={s.xBtn}>✕</button>
        </div>

        <div style={s.body}>
          {error && <div style={s.err}>⚠️ {error}</div>}

          {/* انتخاب حالت */}
          <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
            {MODES.map(m => (
              <button key={m.key} onClick={()=>{ setMode(m.key); setChecked({}); }}
                style={{
                  padding:'7px 14px', borderRadius:'8px', cursor:'pointer',
                  border: mode===m.key ? `2px solid ${m.color}` : '2px solid #ddd',
                  background: mode===m.key ? m.color+'18' : '#fff',
                  color: mode===m.key ? m.color : '#666',
                  fontFamily:'inherit', fontSize:'12px', fontWeight: mode===m.key?'700':'400',
                }}>
                {m.icon} {m.label}
              </button>
            ))}
          </div>

          {/* سند مقصد */}
          <div style={{...s.card, background:'#fffdf5', border:'1.5px solid #f0e6c0'}}>
            <div style={{fontSize:'12px',fontWeight:'700',color:'#8d6e00',marginBottom:'8px'}}>🧾 سند مقصد</div>
            <div style={{display:'flex',gap:'14px',flexWrap:'wrap',alignItems:'flex-end'}}>
              <div style={s.fld}>
                <label style={s.lbl}>شماره سند مقصد</label>
                <input value={toFa(readyNo)}
                  onChange={e=>setReadyNo(toEng(e.target.value).replace(/\D/g,'').slice(0,6))}
                  placeholder="خالی = سند جدید" style={{...s.inp,width:'150px'}} />
              </div>
              {isNewDoc && (
                <div style={s.fld}>
                  <label style={s.lbl}>تاریخ سند جدید *</label>
                  <JalaliDatePicker value={docDate} onChange={v=>setDocDate(v)} placeholder="تاریخ"/>
                </div>
              )}
              <div style={s.fld}>
                <label style={s.lbl}>از تاریخ</label>
                <JalaliDatePicker value={fromDate} onChange={v=>{setFromDate(v);}} placeholder="1394/07/01"/>
              </div>
            </div>
          </div>

          {/* جستجو */}
          <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
            <div style={{...s.fld,flex:1,minWidth:'200px'}}>
              <label style={s.lbl}>جستجو (پیمانکار / قرارداد / شماره / سپام)</label>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="جستجو..." style={{...s.inp,width:'100%'}} />
            </div>
            <button onClick={load} disabled={loading} style={s.btnS}>
              {loading?'⏳...':'↺ به‌روزرسانی'}
            </button>
            <span style={{fontSize:'11px',color:'#888'}}>
              {toFa(shownCount)} از {toFa(filteredCount)} ردیف
            </span>
          </div>

          {/* جدول */}
          <div style={{flex:1,overflow:'auto',borderRadius:'8px',boxShadow:'0 1px 6px rgba(0,0,0,0.1)'}}>
            <table style={{width:'100%',borderCollapse:'collapse',direction:'rtl',fontSize:'11px',tableLayout:'fixed'}}>
              {/* سرستون‌ها */}
              <thead>
                <tr>
                  <TH idx={0}><span onClick={()=>toggleAll(!allVisibleChecked)} title="انتخاب همه" style={{cursor:'pointer',fontWeight:'700'}}>{allVisibleChecked?'✓':'▢'}</span></TH>
                  <TH idx={1}>سریال</TH>
                  <TH idx={2}>پرونده</TH>
                  <TH idx={3} right>پیمانکار</TH>
                  <TH idx={4}>نوع تضمین</TH>
                  <TH idx={5}>موضوع</TH>
                  <TH idx={6}>شماره</TH>
                  <TH idx={7}>سپام</TH>
                  <TH idx={8}>تاریخ صدور</TH>
                  {mode === 'decrease' ? <>
                    <TH idx={9} red>مبلغ اولیه</TH>
                    <TH idx={10} red>مبلغ قبلی</TH>
                    <TH idx={11} red>کاهش/افزایش</TH>
                  </> : <>
                    <TH idx={9} red>مبلغ</TH>
                  </>}
                  <TH idx={mode==='decrease'?12:10}>قرارداد</TH>
                  {(mode==='cancel'||mode==='give') && <TH idx={11}>
                    {mode==='cancel'?'شماره ابطال':'شماره ضبط'}
                  </TH>}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r,i)=>{
                  const k = rowKey(r);
                  return (
                  <tr key={k} onClick={()=>toggleOne(k,!checked[k])} style={{background:checked[k]?'#c8e6c9':i%2===0?'#fff':'#fafafa',cursor:'pointer'}}>
                    <td style={{...s.td,fontWeight:'700',color:checked[k]?'#1b5e20':'#bbb'}}>{checked[k]?'✓':''}</td>
                    <td style={s.td}>{toFa(r.serial_no)}</td>
                    <td style={s.td}>{r.case_no||'—'}</td>
                    <td style={{...s.td,textAlign:'right'}}>{r.contractor_name}</td>
                    <td style={{...s.td,fontSize:'9px'}}>{r.zemanat_kind_name||'—'}</td>
                    <td style={{...s.td,fontSize:'9px'}}>{r.assurance_subject_name||'—'}</td>
                    <td style={s.td}>{r.zemanat_no}</td>
                    <td style={{...s.td,fontSize:'10px'}}>{r.sepam||'—'}</td>
                    <td style={{...s.td,fontFamily:'monospace'}}>{r.issu_date}</td>
                    {mode === 'decrease' ? <>
                      <td style={{...s.td,fontFamily:'monospace'}}>{numFmt(r.amount)}</td>
                      <td style={{...s.td,fontFamily:'monospace'}}>{numFmt(r.previous_amount)}</td>
                      <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',
                        color: r.change_amount > 0 ? '#b71c1c' : '#1b5e20'}}>
                        {r.change_amount > 0 ? `−${numFmt(r.decrease_amount)}` : `+${numFmt(r.increase_amount)}`}
                      </td>
                    </> : <>
                      <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:'#b71c1c'}}>
                        {numFmt(r.amount)}
                      </td>
                    </>}
                    <td style={{...s.td,fontSize:'9px',maxWidth:'140px',overflow:'hidden',textOverflow:'ellipsis'}}>
                      {r.contract_display||'—'}
                    </td>
                    {mode==='cancel' && <td style={s.td}>{r.cancel_no||'—'}</td>}
                    {mode==='give' && <td style={s.td}>{r.give_no||'—'}</td>}
                  </tr>
                );})}
                {!loading && visibleRows.length===0 && (
                  <tr><td colSpan={15} style={{...s.td,padding:'24px',color:'#999'}}>
                    📭 ضمانت‌نامه‌ای یافت نشد
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <LoadMoreButton />

          {/* فوتر */}
          <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',flexShrink:0}}>
            <span style={s.badge(modeInfo.color)}>{toFa(selectedRows.length)} ردیف</span>
            <span style={s.badge('#b71c1c')}>جمع: {numFmt(totalAmount)}</span>
            <button onClick={addToDoc} disabled={saving||!selectedRows.length}
              style={{...s.btnP,marginRight:'auto',background:`linear-gradient(135deg,${modeInfo.color},${modeInfo.color}cc)`,
                opacity:(saving||!selectedRows.length)?0.4:1}}>
              {saving?'⏳...':(isNewDoc?'💾 ساخت سند جدید و ثبت':'💾 افزودن به سند')}
            </button>
          </div>
        </div>

        {toast && <div style={{position:'absolute',bottom:'14px',right:'14px',background:'#1b5e20',
          color:'#fff',padding:'10px 20px',borderRadius:'8px',fontSize:'13px',fontWeight:'600',zIndex:10}}>
          {toast}</div>}
      </div>
    </div>
  );
}

/* استایل‌ها */
const s = {
  overlay:   {position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:3000,
               display:'flex',alignItems:'center',justifyContent:'center',padding:'12px'},
  modal:     {background:'#f0f2f7',borderRadius:'14px',width:'100%',maxWidth:'1100px',
               maxHeight:'90vh',display:'flex',flexDirection:'column',
               boxShadow:'0 24px 64px rgba(0,0,0,0.5)',overflow:'hidden',position:'relative'},
  hdr:       {display:'flex',justifyContent:'space-between',alignItems:'flex-start',
               padding:'14px 18px',flexShrink:0},
  xBtn:      {background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',
               color:'#fff',width:'28px',height:'28px',borderRadius:'50%',cursor:'pointer',
               fontSize:'12px',display:'flex',alignItems:'center',justifyContent:'center'},
  body:      {flex:1,overflowY:'auto',padding:'14px',display:'flex',flexDirection:'column',gap:'10px'},
  err:       {background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'7px',
               padding:'8px 12px',color:'#c62828',fontSize:'12px'},
  card:      {background:'#fff',borderRadius:'10px',padding:'14px',
               boxShadow:'0 1px 4px rgba(0,0,0,0.08)'},
  fld:       {display:'flex',flexDirection:'column',gap:'3px'},
  lbl:       {fontSize:'11px',fontWeight:'600',color:'#555'},
  inp:       {padding:'7px 10px',border:'1.5px solid #e0e0e0',borderRadius:'7px',fontSize:'12px',
               fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',
               direction:'rtl',textAlign:'right',boxSizing:'border-box'},
  td:        {padding:'5px 8px',fontSize:'11px',textAlign:'center',
               borderBottom:'1px solid #f0f0f0',whiteSpace:'nowrap',
               overflow:'hidden',textOverflow:'ellipsis'},
  btnP:      {padding:'9px 20px',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',
               fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnS:      {padding:'8px 14px',background:'#e0e0e0',color:'#333',border:'none',
               borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'12px'},
  badge:     bg => ({padding:'5px 12px',background:bg,color:'#fff',
               borderRadius:'16px',fontSize:'12px',fontWeight:'700'}),
};